import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { FilterEmitterService } from '@finder/shared/services/filter-emitter-service/filter-emitter.service';
import { FilterButton } from '@finder/shared/components/filters/classes/filter-button.class';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { LocalStorageService } from '@finder/shared/services/local-storage/local-storage.service';
import { TopFilterButton } from '@finder/shared/components/filters/classes/top-filter-button.class';


const FILTER = {
    FACETS_SELECTED: 'facets-selected',
};

@Component({
    template: ''
})
/* eslint-disable-next-line @angular-eslint/component-class-suffix */
export abstract class BaseFilter implements OnInit, OnDestroy, AfterViewInit {
    @Input() filters;

    appliedFacets: Facet[] = [];
    shrinkButtons = false;
    enableFilters = false;
    topFilters: TopFilterButton[];

    protected destroy$ = new Subject<void>();

    constructor(
        protected filterEmitterService: FilterEmitterService,
        protected finderAnalyticsService: FinderAnalyticsService,
        protected localStorage: LocalStorageService,
    ) {}

    ngOnInit() {
        this.enableFilters = !isEmpty(this.filters);
        this.mapTopFilters();
        localStorage.removeItem(FILTER.FACETS_SELECTED);
    }

    ngAfterViewInit() {
        this.filterEmitterService.watchFilter()
            .pipe(takeUntil(this.destroy$))
            .subscribe((facet: Facet) => {
                this.handleWatchFacet(facet);
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleTopFilter(filter: TopFilterButton) {
        const selected = !filter.selected;
        filter.facets.forEach((facet: Facet) => {
            facet.value = selected;
            this.filterEmitterService.emitFilter(facet);
        });
    }

    setFilterState(filterButton: FilterButton, facet: Facet) {
        const myFacet = filterButton.facets.find(f => f.equal(facet));
        if (myFacet) {
            myFacet.value = facet.value;
        }
        filterButton.setSelectedState();
    }

    clearPills() {
        this.appliedFacets.forEach(facet => {
            facet.value = false;
            this.filterEmitterService.emitFilter(facet);
        });
    }

    closePill(facet: Facet) {
        facet.value = false;
        this.filterEmitterService.emitFilter(facet);
    }

    protected applyFacet(facet: Facet) {
        const appliedFacet = this.appliedFacets.find(f => f.equal(facet));

        if (facet.value && !appliedFacet) {
            this.appliedFacets.push(facet);
        } else if (!facet.value && appliedFacet) {
            this.appliedFacets = this.appliedFacets.filter(f => !f.equal(facet));
        }
        this.finderAnalyticsService.updateAnalyticsFacets(this.appliedFacets, facet.id);
        this.filterEmitterService.emitFilterList(this.appliedFacets);
        this.localStorage.set(FILTER.FACETS_SELECTED, this.appliedFacets);
    }

    private mapTopFilters() {
        if (!this.filters.topFilters) {
            return;
        }

        this.topFilters = this.filters.topFilters.map(filter => new TopFilterButton(
            filter.id,
            filter.label,
            filter.icon,
            this.filters.labels.buttonAriaLabel,
            filter.facets,
            filter.preSelected
        ));

        this.topFilters.forEach(filter => {
            if (filter.preSelected) {
                this.toggleTopFilter(filter);
            }
        });
    }

    abstract handleWatchFacet(facet: Facet);
}
